const SUPABASE_STORAGE_PREFIX = `sb-${import.meta.env["VITE_SUPABASE_PROJECT_ID"]}-`;

export const ADMIN_LOGIN_INTENT_KEY = 'admin_login_intent';
export const ADMIN_MFA_PENDING_KEY = 'admin_mfa_pending';
export const ADMIN_MFA_VERIFIED_KEY = 'admin_mfa_verified';
export const ADMIN_SESSION_START_KEY = 'admin_session_start';

type StorageTarget = 'session' | 'local' | 'all';

const withSessionStorage = (callback: (storage: Storage) => void) => {
  try {
    callback(sessionStorage);
  } catch {
    /* no-op */
  }
};

const withLocalStorage = (callback: (storage: Storage) => void) => {
  try {
    callback(localStorage);
  } catch {
    /* no-op */
  }
};

const setValue = (key: string, value: string, target: StorageTarget = 'all') => {
  if (target === 'session' || target === 'all') {
    withSessionStorage((storage) => storage.setItem(key, value));
  }
  if (target === 'local' || target === 'all') {
    withLocalStorage((storage) => storage.setItem(key, value));
  }
};

const removeValue = (key: string, target: StorageTarget = 'all') => {
  if (target === 'session' || target === 'all') {
    withSessionStorage((storage) => storage.removeItem(key));
  }
  if (target === 'local' || target === 'all') {
    withLocalStorage((storage) => storage.removeItem(key));
  }
};

const readSessionValue = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const readAnyValue = (key: string) => {
  const sessionValue = readSessionValue(key);
  if (sessionValue) return sessionValue;

  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const markAdminLoginIntent = () => setValue(ADMIN_LOGIN_INTENT_KEY, 'true', 'session');

export const clearAdminLoginIntent = () => removeValue(ADMIN_LOGIN_INTENT_KEY, 'all');

export const hasAdminLoginIntent = () => readSessionValue(ADMIN_LOGIN_INTENT_KEY) === 'true';

export const markAdminMfaPending = () => setValue(ADMIN_MFA_PENDING_KEY, 'true', 'all');

export const clearAdminMfaPending = () => removeValue(ADMIN_MFA_PENDING_KEY, 'all');

export const hasAdminMfaPending = () => readAnyValue(ADMIN_MFA_PENDING_KEY) === 'true';

export const markAdminMfaVerified = () => {
  clearAdminMfaPending();
  clearAdminLoginIntent();
  // Store in BOTH session and local storage so all tabs/windows share the state
  setValue(ADMIN_MFA_VERIFIED_KEY, 'true', 'all');
  setValue(ADMIN_SESSION_START_KEY, Date.now().toString(), 'all');
};

export const clearAdminMfaVerified = () => removeValue(ADMIN_MFA_VERIFIED_KEY, 'all');

export const hasAdminMfaVerified = () => readAnyValue(ADMIN_MFA_VERIFIED_KEY) === 'true';

export const clearAdminFlowState = () => {
  clearAdminLoginIntent();
  clearAdminMfaPending();
  clearAdminMfaVerified();
  removeValue(ADMIN_SESSION_START_KEY, 'all');
};

export const clearPostAuthRedirectState = () => removeValue('post_auth_redirect', 'all');

export const purgeStoredSupabaseSession = () => {
  withSessionStorage((storage) => {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(SUPABASE_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  });

  withLocalStorage((storage) => {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(SUPABASE_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  });
};

export const resetAdminClientState = ({ purgeSupabaseSession = false }: { purgeSupabaseSession?: boolean } = {}) => {
  clearAdminFlowState();
  clearPostAuthRedirectState();

  if (purgeSupabaseSession) {
    purgeStoredSupabaseSession();
  }
};